import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface PaginationProps {
  page: number;          // 1-based
  pageCount: number;     // total pages
  onPageChange: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  /** Ile numerów pokazać przy bieżącej stronie (po obu stronach). Default 1. */
  siblingCount?: number;
  className?: string;
}

/**
 * Generuje listę stron z ellipsis. Zawsze pokazuje pierwszą i ostatnią.
 * [1, '...', 4, 5, 6, '...', 20] style.
 */
function getPageRange(page: number, pageCount: number, siblingCount: number): (number | 'ellipsis')[] {
  if (pageCount <= 7 + siblingCount * 2) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(page - siblingCount, 1);
  const rightSibling = Math.min(page + siblingCount, pageCount);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < pageCount - 1;

  const result: (number | 'ellipsis')[] = [1];
  if (showLeftEllipsis) result.push('ellipsis');
  for (let i = Math.max(2, leftSibling); i <= Math.min(pageCount - 1, rightSibling); i++) {
    result.push(i);
  }
  if (showRightEllipsis) result.push('ellipsis');
  if (pageCount > 1) result.push(pageCount);
  return result;
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [20, 50, 100],
  siblingCount = 1,
  className,
}: PaginationProps) {
  if (pageCount <= 1 && !onPageSizeChange) return null;

  const range = getPageRange(page, pageCount, siblingCount);
  const canPrev = page > 1;
  const canNext = page < pageCount;

  const btnBase = 'inline-flex items-center justify-center min-w-[32px] h-8 px-2 rounded-lg text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40';

  return (
    <div className={['flex items-center justify-between gap-3 flex-wrap', className].filter(Boolean).join(' ')}>
      {/* Page numbers */}
      {pageCount > 1 && (
        <nav aria-label="Paginacja" className="flex items-center gap-1">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => canPrev && onPageChange(page - 1)}
            className={`${btnBase} text-text-secondary hover:bg-surface-hover hover:text-text-primary disabled:opacity-40 disabled:cursor-not-allowed`}
            aria-label="Poprzednia strona"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          {range.map((p, idx) =>
            p === 'ellipsis' ? (
              <span key={`e-${idx}`} className="inline-flex items-center justify-center min-w-[32px] h-8 text-xs text-text-disabled" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                aria-current={p === page ? 'page' : undefined}
                className={`${btnBase} ${
                  p === page
                    ? 'bg-accent-primary/15 text-accent-primary border border-accent-primary/30'
                    : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary border border-transparent'
                }`}
              >
                {p}
              </button>
            ),
          )}
          <button
            type="button"
            disabled={!canNext}
            onClick={() => canNext && onPageChange(page + 1)}
            className={`${btnBase} text-text-secondary hover:bg-surface-hover hover:text-text-primary disabled:opacity-40 disabled:cursor-not-allowed`}
            aria-label="Następna strona"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </nav>
      )}

      {/* Page size selector */}
      {pageSize !== undefined && onPageSizeChange && (
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <label htmlFor="pagination-size">Na stronie:</label>
          <select
            id="pagination-size"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="bg-surface-base/60 border border-border rounded-md px-2 py-1 text-text-primary text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40"
          >
            {pageSizeOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
